// @ts-ignore
import { jitsiLocalStorage } from '@jitsi/js-utils/jitsi-local-storage';
import { browser } from '../lib-jitsi-meet';
import { inIframe } from '../util/iframeUtils';
import { parseURLParams } from '../util/parseURLParams';
import logger from './logger';
/**
 * Handles changes of the fake local storage.
 *
 * @returns {void}
 */
function onFakeLocalStorageChanged() {
    APP.API.notifyLocalStorageChanged(jitsiLocalStorage.serialize(['jitsiLocalStorage']));
}
/**
 * Checks if the local storage of the host page needs to be used instead jitsi-meet's local storage.
 *
 * @param {Object} urlParams - Object with parsed URL params.
 * @returns {boolean} - True if the local storage of the host page needs to be used instead jitsi-meet's local storage
 * and false otherwise.
 */
function shouldUseHostPageLocalStorage(urlParams) {
    // NOTE: normally the url params and the config will be merged into the redux store. But we want to setup the local
    // storage as soon as possible, the store is not created yet and the merging of the URL params and the config
    // haven't been executed yet. That's why we need to manually parse the URL params and also access the config through
    // the global variable.
    if (urlParams['config.useHostPageLocalStorage'] === true
        || (typeof config === 'object' && config.useHostPageLocalStorage)) {
        return true;
    }
    if (jitsiLocalStorage.isLocalStorageDisabled()) { // We have detected that ou own local storage is not working.
        return true;
    }
    if (browser.isWebKitBased() && inIframe()) {
        // WebKit browsers don't persist local storage for third-party iframes.
        return true;
    }
    return false;
}
/**
 * Performs initial setup of the jitsiLocalStorage.
 *
 * @returns {void}
 */
/**
 * Safely initializes Jitsi localStorage from URL params or falls back to defaults.
 * Handles corruption, cross-domain restrictions, and Safari Private Mode.
 */
function setupJitsiLocalStorage() {
    // @ts-ignore
    const urlParams = parseURLParams(window.location);
    if (!shouldUseHostPageLocalStorage(urlParams)) {
        return;
    }
    // 1. Safely extract and parse localStorage content
    let localStorageContent = {};
    try {
        const localStorageStr = urlParams['appData.localStorageContent'];
        // Skip parsing if empty/undefined
        if (localStorageStr && typeof localStorageStr === 'string') {
            localStorageContent = safeJsonParse(localStorageStr) || {};
            // Validate parsed data is an object
            if (typeof localStorageContent !== 'object' || Array.isArray(localStorageContent)) {
                throw new Error('Parsed data is not a plain object');
            }
        }
    }
    catch (error) {
        logger.error('Failed to parse localStorageContent. Using empty fallback.', error, urlParams['appData.localStorageContent']);
        localStorageContent = {};
    }
    // 2. Cross-browser storage availability check
    try {
        // Disable real localStorage first (Brave/Safari cross-domain protection)
        jitsiLocalStorage.setLocalStorageDisabled(true);
        // 3. Only proceed if we have valid data
        if (typeof localStorageContent === 'object' && !Array.isArray(localStorageContent)) {
            Object.entries(localStorageContent).forEach(([key, value]) => {
                try {
                    jitsiLocalStorage.setItem(key, value);
                }
                catch (setError) {
                    logger.warn(`Failed to set localStorage key "${key}"`, setError);
                }
            });
        }
        logger.info(`Initialized ${Object.keys(localStorageContent).length} localStorage items`);
    }
    catch (error) {
        // Critical failure - restore real localStorage if possible
        logger.error('Fatal localStorage initialization error', error);
        jitsiLocalStorage.setLocalStorageDisabled(false);
    }
    // 4. Set up change listener (no try-catch needed here - failures are non-critical)
    jitsiLocalStorage.on('changed', onFakeLocalStorageChanged);
}
// Helper function (define this if not already available)
function safeJsonParse(str) {
    try {
        return JSON.parse(str);
    }
    catch (e) {
        // Special case for "undefined" string
        if (str === 'undefined')
            return undefined;
        throw e;
    }
}
setupJitsiLocalStorage();
